import { useMemo, useState } from 'react';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { SELECT_CLASS } from '../../lib/selectClass';
import { useLevelEntities } from '../../hooks/useLevelEntities';
import { levelName } from '../../lib/scopeLabels';
import type { LabelResolver } from '../../hooks/useLabelResolver';
import type { LevelEntityListItemLocal } from '../../api/types/LevelEntityLocal';
import type { Scope } from '../../api/types/permissions';

/** Scopes de nivel genérico (data-driven, DEC-RBAC-090). El destino de un rol LEVEL_N es una entidad de ese nivel. */
export const GENERIC_LEVELS = ['LEVEL_1', 'LEVEL_2', 'LEVEL_3'] as const;
export type GenericLevel = (typeof GENERIC_LEVELS)[number];

/** Type guard: ¿el scope es un nivel genérico (LEVEL_1/2/3)? */
export const isGenericLevel = (s: Scope | null | undefined): s is GenericLevel =>
    !!s && (GENERIC_LEVELS as readonly string[]).includes(s);

interface Props {
    /** Tenant donde viven las entidades de nivel (drill-down platform o el del actor). */
    tenantId: string;
    /** Nivel DESTINO = scope REAL del rol elegido. Decide la cascada de padres a mostrar. */
    level: GenericLevel;
    /** Ids de entidades HOJA (del nivel destino) marcadas — controlado por el padre. */
    value: string[];
    /** Notifica los ids hoja marcados (≥1 requerido lo valida el padre). */
    onChange: (ids: string[]) => void;
    /** Prefijo para los `id`/`htmlFor` (evita colisiones si hay 2 instancias montadas). */
    idPrefix: string;
    /** Solo dispara queries cuando el contenedor (modal/dialog) está visible. Default true. */
    open?: boolean;
    /** Resolvedor del tenant donde se asigna; da el nombre de cada nivel. */
    labels?: LabelResolver;
}

/** Orden alfabético por `displayName` (default de presentación del proyecto). */
export function sortByName(items: LevelEntityListItemLocal[]): LevelEntityListItemLocal[] {
    return [...items].sort((a, b) =>
        a.displayName.localeCompare(b.displayName, 'es', { sensitivity: 'base' }),
    );
}

/**
 * Picker de entidades de NIVEL genérico para el DESTINO de un rol LEVEL_N (DEC-RBAC-090).
 * Según el `level` del rol elegido:
 *   - LEVEL_1 → multi-select de entidades raíz (sin padre).
 *   - LEVEL_2 → elegir un padre LEVEL_1 (single) → multi-select de sus hijos LEVEL_2.
 *   - LEVEL_3 → elegir padre LEVEL_1 → padre LEVEL_2 → multi-select de sus hijos LEVEL_3.
 *
 * LIMITACIÓN (TD-WEBAPP-031): la selección de hojas es dentro de UNA sola cadena de padres elegida
 * (un padre por nivel). No soporta marcar hojas de padres distintos en la misma alta/asignación
 * (cross-parent). Para eso hoy se repite la operación por cada padre.
 *
 * Componente CONTROLADO: el padre posee `value` (ids hoja) y lo usa para `canSubmit`/`scopeRefs`. El
 * estado de la CADENA de padres (parentL1/parentL2) es interno; al cambiar un padre se limpia la
 * selección de aguas abajo (`onChange([])`). Para reiniciar TODO al cambiar de rol, el padre debe
 * remontar el picker con `key={level}`.
 */
export function LevelEntityPicker({
    tenantId,
    level,
    value,
    onChange,
    idPrefix,
    open = true,
    labels,
}: Props) {
    // Cadena de padres elegida (single-select por nivel).
    const [parentL1, setParentL1] = useState('');
    const [parentL2, setParentL2] = useState('');

    const needL2 = level === 'LEVEL_2' || level === 'LEVEL_3';
    const needL3 = level === 'LEVEL_3';

    // Los tres hooks se llaman SIEMPRE (regla de hooks); se gatean pasando `tenantId` undefined cuando
    // ese nivel no aplica o falta su padre (el hook ya hace `enabled: !!tenantId`).
    const l1 = useLevelEntities(open ? tenantId : undefined, 'LEVEL_1');
    const l2 = useLevelEntities(
        open && needL2 && parentL1 ? tenantId : undefined,
        'LEVEL_2',
        parentL1 || undefined,
    );
    const l3 = useLevelEntities(
        open && needL3 && parentL2 ? tenantId : undefined,
        'LEVEL_3',
        parentL2 || undefined,
    );

    const l1Items = useMemo(() => sortByName(l1.data ?? []), [l1.data]);
    const l2Items = useMemo(() => sortByName(l2.data ?? []), [l2.data]);
    const l3Items = useMemo(() => sortByName(l3.data ?? []), [l3.data]);

    // Query + items del nivel DESTINO (donde se multiseleccionan las hojas).
    const targetQuery = level === 'LEVEL_1' ? l1 : level === 'LEVEL_2' ? l2 : l3;
    const targetItems = level === 'LEVEL_1' ? l1Items : level === 'LEVEL_2' ? l2Items : l3Items;
    // ¿Está lista la cadena de padres para mostrar las hojas?
    const parentReady = level === 'LEVEL_1' || (needL3 ? !!parentL2 : !!parentL1);

    const handleParentL1 = (id: string): void => {
        setParentL1(id);
        setParentL2('');
        onChange([]);
    };
    const handleParentL2 = (id: string): void => {
        setParentL2(id);
        onChange([]);
    };
    const toggle = (id: string, checked: boolean): void => {
        onChange(checked ? [...value, id] : value.filter((x) => x !== id));
    };

    return (
        <div className="space-y-3">
            {needL2 && (
                <ParentSelect
                    id={`${idPrefix}-parent-l1`}
                    label={`${levelName(labels, 'LEVEL_1')} (padre)`}
                    placeholder={`Elige ${levelName(labels, 'LEVEL_1')}`}
                    items={l1Items}
                    value={parentL1}
                    onChange={handleParentL1}
                    error={l1.isError}
                />
            )}

            {needL3 && (
                <ParentSelect
                    id={`${idPrefix}-parent-l2`}
                    label={`${levelName(labels, 'LEVEL_2')} (padre)`}
                    placeholder={`Elige ${levelName(labels, 'LEVEL_2')}`}
                    items={l2Items}
                    value={parentL2}
                    onChange={handleParentL2}
                    // Requiere primero un padre L1; hasta entonces sin opciones.
                    disabled={!parentL1}
                    error={l2.isError}
                />
            )}

            <div className="space-y-1">
                <Label>{`Destino (${levelName(labels, level)})`}</Label>
                {!parentReady ? (
                    <p className="text-sm text-muted-foreground">
                        Elige primero {needL3 ? 'los niveles superiores' : 'el nivel superior'}.
                    </p>
                ) : (
                    <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-input p-3">
                        {targetQuery.isError ? (
                            <p className="text-sm text-destructive">
                                No se pudieron cargar las entidades. Intenta de nuevo.
                            </p>
                        ) : targetItems.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No hay entidades en este nivel
                            </p>
                        ) : (
                            targetItems.map((e) => (
                                <label key={e.id} className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                        checked={value.includes(e.id)}
                                        onCheckedChange={(c) => {
                                            toggle(e.id, c === true);
                                        }}
                                    />
                                    {e.displayName}
                                </label>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

interface ParentSelectProps {
    id: string;
    label: string;
    placeholder: string;
    items: LevelEntityListItemLocal[];
    value: string;
    onChange: (id: string) => void;
    disabled?: boolean;
    error?: boolean;
}

/** `<select>` single de un padre de la cadena. Presentacional; opciones ya ordenadas alfabéticamente. */
export function ParentSelect({
    id,
    label,
    placeholder,
    items,
    value,
    onChange,
    disabled = false,
    error = false,
}: ParentSelectProps) {
    return (
        <div className="space-y-1">
            <Label htmlFor={id}>{label}</Label>
            <select
                id={id}
                className={SELECT_CLASS}
                value={value}
                disabled={disabled}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {items.map((it) => (
                    <option key={it.id} value={it.id}>
                        {it.displayName}
                    </option>
                ))}
            </select>
            {error && (
                <p className="text-sm text-destructive">
                    No se pudieron cargar las entidades. Intenta de nuevo.
                </p>
            )}
        </div>
    );
}
